/**
 * geometryStore — persisted state for the geometry pipeline panel.
 *
 * Tracks the current assembly, sweep settings, and transfer status
 * across panel tab switches. Uses Zustand with no persistence middleware
 * (session-scoped — assemblies are transient until uploaded to Flow360).
 */

import { create } from "zustand";
import type {
  Assembly,
  CutDefinition,
  DeflectionSweep,
  JobStatus,
  TransferResult,
  CaseCreationResult,
} from "../api/geometry";

export type SweepMode = "full_factorial" | "one_at_a_time" | "paired";

interface GeometryState {
  assembly: Assembly | null;
  sweep: DeflectionSweep | null;
  transferResult: TransferResult | null;
  caseResults: CaseCreationResult | null;
  loading: boolean;
  error: string | null;

  /** Per-surface angle strings (raw user input, e.g. "-20:5:20") */
  surfaceAngles: Record<string, string>;
  sweepMode: SweepMode;

  /** Cut definitions for the assembly */
  cutDefinitions: CutDefinition[];

  /** Current deflection angle for preview */
  previewDeltaDeg: number;

  /** Background job tracking */
  activeJobId: string | null;
  jobStatus: JobStatus | null;

  /** Deflected sweep job (generates all STEPs) */
  sweepJobId: string | null;
  sweepJobStatus: JobStatus | null;

  // Actions
  setAssembly: (assembly: Assembly | null) => void;
  setSweep: (sweep: DeflectionSweep | null) => void;
  setTransferResult: (result: TransferResult | null) => void;
  setCaseResults: (result: CaseCreationResult | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSurfaceAngles: (angles: Record<string, string>) => void;
  setSweepMode: (mode: SweepMode) => void;
  setCutDefinitions: (cuts: CutDefinition[]) => void;
  setPreviewDeltaDeg: (deg: number) => void;
  setActiveJobId: (id: string | null) => void;
  setJobStatus: (status: JobStatus | null) => void;
  setSweepJobId: (id: string | null) => void;
  setSweepJobStatus: (status: JobStatus | null) => void;
  reset: () => void;
}

const initialState = {
  assembly: null,
  sweep: null,
  transferResult: null,
  caseResults: null,
  loading: false,
  error: null,
  surfaceAngles: {},
  sweepMode: "full_factorial" as SweepMode,
  cutDefinitions: [] as CutDefinition[],
  previewDeltaDeg: 0,
  activeJobId: null,
  jobStatus: null,
  sweepJobId: null,
  sweepJobStatus: null,
};

export const useGeometryStore = create<GeometryState>((set) => ({
  ...initialState,
  setAssembly: (assembly) =>
    set({
      assembly,
      surfaceAngles: assembly
        ? Object.fromEntries(
            assembly.bodies
              .filter((b) => b.role === "control_surface")
              .map((b) => [b.name, "-20, -10, 0, 10, 20"]),
          )
        : {},
      sweep: null,
      transferResult: null,
      caseResults: null,
      cutDefinitions: [],
      activeJobId: null,
      jobStatus: null,
      sweepJobId: null,
      sweepJobStatus: null,
    }),
  setSweep: (sweep) => set({ sweep }),
  setTransferResult: (transferResult) => set({ transferResult }),
  setCaseResults: (caseResults) => set({ caseResults }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error, loading: false }),
  setSurfaceAngles: (surfaceAngles) => set({ surfaceAngles }),
  setSweepMode: (sweepMode) => set({ sweepMode }),
  setCutDefinitions: (cutDefinitions) => set({ cutDefinitions }),
  setPreviewDeltaDeg: (previewDeltaDeg) => set({ previewDeltaDeg }),
  setActiveJobId: (activeJobId) => set({ activeJobId }),
  setJobStatus: (jobStatus) => set({ jobStatus }),
  setSweepJobId: (sweepJobId) => set({ sweepJobId }),
  setSweepJobStatus: (sweepJobStatus) => set({ sweepJobStatus }),
  reset: () => set(initialState),
}));
